import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { AlertCircle, ArrowLeft, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { FamilyDto } from './types'
import { getCatalogFamilies } from './sales-catalog.api'
import { filterGroups, filterVariants, groupFamilies, type FamilyGroup } from './group-families'

interface SelectSalesItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectFamily: (family: FamilyDto) => void
  projectId: string
}

export function SelectSalesItemDialog({ open, onOpenChange, onSelectFamily, projectId }: SelectSalesItemDialogProps) {
  const [families, setFamilies] = useState<FamilyDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null)

  // Reset transient picker state every time the dialog closes — `handleClose` only
  // fires on Radix-internal close paths (Esc / overlay / X button). When the parent
  // closes the dialog directly via `setOpen(false)` after `onSelectFamily`, the
  // group drill-down would persist and the next open would land mid-group instead
  // of the top level. This effect keeps the close path consistent across triggers.
  useEffect(() => {
    if (!open) {
      setQuery('')
      setSelectedGroupKey(null)
      setError(null)
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    let isMounted = true

    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await getCatalogFamilies(projectId)
        if (isMounted) {
          setFamilies(response || [])
        }
      } catch {
        if (isMounted) {
          setError('Erreur lors du chargement du catalogue')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    load()

    return () => {
      isMounted = false
    }
  }, [open, projectId])

  const groups: FamilyGroup[] = useMemo(() => groupFamilies(families), [families])
  const visibleGroups = useMemo(() => filterGroups(groups, query), [groups, query])

  const selectedGroup = useMemo(
    () => (selectedGroupKey ? groups.find((g) => g.groupKey === selectedGroupKey) ?? null : null),
    [selectedGroupKey, groups]
  )
  const visibleVariants = useMemo(
    () => (selectedGroup ? filterVariants(selectedGroup.variants, query) : []),
    [selectedGroup, query]
  )

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setQuery('')
      setSelectedGroupKey(null)
      setError(null)
    }
    onOpenChange(nextOpen)
  }

  const handleGroupClick = (group: FamilyGroup) => {
    if (group.variants.length === 1) {
      // Mono-product group: skip the second screen, select directly.
      onSelectFamily(group.variants[0])
      return
    }
    setSelectedGroupKey(group.groupKey)
    setQuery('')
  }

  const handleBack = () => {
    setSelectedGroupKey(null)
    setQuery('')
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selectedGroup ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  onClick={handleBack}
                  data-testid="catalog-group-back-btn"
                  aria-label="Retour"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <span>{selectedGroup.groupLabel}</span>
              </>
            ) : (
              'Ajouter une ligne'
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="catalog-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              placeholder={selectedGroup ? `Rechercher dans ${selectedGroup.groupLabel}…` : 'Rechercher une famille...'}
              autoFocus
            />
          </div>

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Chargement...</div>
          ) : (
            <div className="max-h-[65vh] overflow-y-auto pr-1">
              {selectedGroup ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {visibleVariants.map((variant) => (
                      <button
                        key={variant.familyId}
                        type="button"
                        data-testid="catalog-variant-btn"
                        onClick={() => onSelectFamily(variant)}
                        className={
                          'flex flex-col items-center justify-center gap-2 rounded-lg border px-3 py-4 text-center transition-colors ' +
                          'hover:bg-accent hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
                        }
                      >
                        <span className="text-3xl leading-none">{variant.icon}</span>
                        <div className="text-sm font-medium leading-tight">{variant.familyLabel}</div>
                      </button>
                    ))}
                  </div>

                  {visibleVariants.length === 0 && (
                    <div className="py-10 text-center">
                      <p className="text-sm text-muted-foreground">Aucun résultat</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {visibleGroups.map((group) => (
                      <button
                        key={group.groupKey}
                        type="button"
                        data-testid="catalog-group-btn"
                        onClick={() => handleGroupClick(group)}
                        className={
                          'relative flex flex-col items-center justify-center gap-2 rounded-lg border px-3 py-4 text-center transition-colors ' +
                          'hover:bg-accent hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
                        }
                      >
                        <span className="text-3xl leading-none">{group.icon}</span>
                        <div className="text-sm font-medium leading-tight">{group.groupLabel}</div>
                        {group.variants.length > 1 ? (
                          <span className="absolute top-1.5 right-2 text-[10px] text-muted-foreground tabular-nums">
                            {group.variants.length}
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>

                  {visibleGroups.length === 0 && (
                    <div className="py-10 text-center">
                      <p className="text-sm text-muted-foreground">Aucun résultat</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => handleClose(false)}>
              Fermer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
